import { inject } from '@angular/core';
import { Routes } from '@angular/router';
import { authGuard } from './auth/guards/auth.guard';
import { modulePermissionGuard } from './auth/guards/module-permission.guard';
import { sessionGuard } from './auth/guards/session.guard';
import { AUTH_ROUTE_PATH } from './auth/models/auth-user.model';
import { APP_MODULE } from './auth/models/session.model';
import { AuthStateService } from './auth/services/auth-state.service';
import { scopeGuard } from './scope/guards/scope.guard';
import { getFirstAccessibleModulePath } from './shared/models/sidenav.model';
import { devOnlyGuard } from './showcase/guards/dev-only.guard';

function resolveHomePath(): string {
  const auth = inject(AuthStateService);
  const path = getFirstAccessibleModulePath((module) => auth.hasModuleAccess(module));

  return path ?? `/${AUTH_ROUTE_PATH.NO_ACCESS}`;
}

export const routes: Routes = [
  {
    path: 'design-system',
    canMatch: [devOnlyGuard],
    loadChildren: () => import('./showcase/showcase.routes').then((m) => m.SHOWCASE_ROUTES),
  },
  {
    path: AUTH_ROUTE_PATH.LOGIN,
    loadChildren: () => import('./auth/auth.routes').then((m) => m.AUTH_ROUTES),
  },
  {
    path: AUTH_ROUTE_PATH.NO_ROLE,
    canMatch: [authGuard],
    loadComponent: () =>
      import('./auth/pages/no-role-page/no-role-page.component').then((m) => m.NoRolePageComponent),
    title: 'Sin rol asignado',
  },
  {
    path: AUTH_ROUTE_PATH.NO_ACCESS,
    canMatch: [authGuard],
    loadComponent: () =>
      import('./auth/pages/no-access-page/no-access-page.component').then(
        (m) => m.NoAccessPageComponent,
      ),
    title: 'Sin acceso',
  },
  {
    path: '',
    canMatch: [authGuard, sessionGuard, scopeGuard],
    loadComponent: () =>
      import('./shared/pages/main-layout/main-layout.component').then((m) => m.MainLayoutComponent),
    children: [
      { path: '', pathMatch: 'full', redirectTo: resolveHomePath },
      {
        path: 'dashboard',
        canMatch: [modulePermissionGuard(APP_MODULE.DASHBOARD)],
        loadChildren: () => import('./dashboard/dashboard.routes').then((m) => m.DASHBOARD_ROUTES),
      },
      {
        path: 'entregas',
        canMatch: [modulePermissionGuard(APP_MODULE.DELIVERIES)],
        loadChildren: () => import('./entregas/entregas.routes').then((m) => m.ENTREGAS_ROUTES),
      },
      {
        path: 'cedulas',
        canMatch: [modulePermissionGuard(APP_MODULE.SCHEDULES)],
        loadChildren: () => import('./cedulas/cedulas.routes').then((m) => m.CEDULAS_ROUTES),
      },
      {
        path: 'usuarios',
        canMatch: [modulePermissionGuard(APP_MODULE.USERS)],
        loadChildren: () => import('./usuarios/usuarios.routes').then((m) => m.USUARIOS_ROUTES),
      },
      {
        path: 'roles',
        canMatch: [modulePermissionGuard(APP_MODULE.ROLES)],
        loadChildren: () => import('./roles/roles.routes').then((m) => m.ROLES_ROUTES),
      },
      {
        path: 'configuracion',
        canMatch: [
          modulePermissionGuard([
            APP_MODULE.DELIVERY_NONCONFORMITY_TYPES,
            APP_MODULE.OUTPUT_WAREHOUSES,
            APP_MODULE.VEHICLES,
            APP_MODULE.AREAS,
          ]),
        ],
        loadChildren: () =>
          import('./configuracion/configuracion.routes').then((m) => m.CONFIGURACION_ROUTES),
      },
      {
        path: 'incidencias',
        canMatch: [modulePermissionGuard(APP_MODULE.INCIDENTS)],
        loadChildren: () =>
          import('./incidencias/incidencias.routes').then((m) => m.INCIDENCIAS_ROUTES),
      },
      {
        path: 'bitacora-caseta',
        canMatch: [modulePermissionGuard(APP_MODULE.GATE_LOG)],
        loadChildren: () =>
          import('./bitacora-caseta/bitacora-caseta.routes').then((m) => m.BITACORA_CASETA_ROUTES),
      },
      {
        path: '**',
        loadComponent: () =>
          import('./shared/pages/not-found/not-found-page.component').then(
            (m) => m.NotFoundPageComponent,
          ),
        title: 'Página no encontrada',
      },
    ],
  },
  { path: '**', redirectTo: '' },
];
